import { useCallback, useEffect, useState } from 'react';
import { getApiUrl } from '../config';

interface EligibleGroup {
  id: string;
  name: string;
}

interface UseMatchGroupResult {
  eligibleGroups: EligibleGroup[];
  currentGroupId: string | null;
  loading: boolean;
  error: string | null;
  setGroup: (groupId: string | null) => Promise<{ ok: boolean; error?: string }>;
  refresh: () => Promise<void>;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export function useMatchGroup(matchId: string | undefined): UseMatchGroupResult {
  const [eligibleGroups, setEligibleGroups] = useState<EligibleGroup[]>([]);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/matches/${matchId}/eligible-groups`), {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`http ${res.status}`);
      const json = await res.json();
      setEligibleGroups(json.groups || []);
      setCurrentGroupId(json.currentGroupId ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { refresh(); }, [refresh]);

  const setGroup = useCallback(async (groupId: string | null) => {
    if (!matchId) return { ok: false, error: 'no match' };
    try {
      const res = await fetch(getApiUrl(`/matches/${matchId}/group`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ groupId }),
      });
      const json = await res.json();
      if (!res.ok) return { ok: false, error: json.message || json.error };
      setCurrentGroupId(json.currentGroupId);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'patch failed' };
    }
  }, [matchId]);

  return { eligibleGroups, currentGroupId, loading, error, setGroup, refresh };
}
